import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Container, Group, Image, Paper, PasswordInput, Stack, TextInput, Title } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useLoginMutation } from '../api/auth/api'
import { useAuth } from '../auth-provider/auth-context'
import { RouterLink } from '../components/RouterLink'

const loginSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z.string(),
})

type LoginFormData = z.infer<typeof loginSchema>

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const { mutate: login, isPending } = useLoginMutation()
  const navigate = useNavigate()
  const auth = useAuth()

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate({ to: '/app' })
    }
  }, [auth.isAuthenticated, navigate])

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = handleSubmit((formData: LoginFormData) => {
    login(
      { email: formData.email, password: formData.password },
      {
        onSuccess: (response) => {
          auth.login(response.token)
        },
      },
    )
  })

  return (
    <Stack
      align="center"
      justify="center"
      style={{
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #ff8800 0%, #ff6600 35%, #ff4400 100%)',
      }}
    >
      <Container size={600}>
        <Paper withBorder shadow="md" p={30} mt={30} radius="md" w={500}>
          <Image h={40} w="auto" fit="contain" src="/carma_partner_logo.svg" alt="Carma Logo" />

          <Stack mb="sm" mt="md">
            <Title order={3} style={{ fontWeight: 700, color: '#333' }}>
              Sign in to your account
            </Title>
          </Stack>

          <form onSubmit={onSubmit}>
            <TextInput
              label="Email"
              placeholder="you@example.com"
              required
              {...register('email')}
              error={errors.email?.message}
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              mt="md"
              {...register('password')}
              error={errors.password?.message}
            />
            <Group justify="space-between" mt="sm">
              <RouterLink to="/register" size="sm">
                Register
              </RouterLink>
              <RouterLink to="/recovery/forgot" size="sm">
                Forgot password?
              </RouterLink>
            </Group>
            <Button fullWidth mt="xl" type="submit" loading={isPending}>
              Sign in
            </Button>
          </form>
        </Paper>
      </Container>
    </Stack>
  )
}
